import { useMemo, useCallback } from "react";
import { useACLContext } from "../context";
import type { PermissionAction, ResourceType } from "@shield-acl/core";

/**
 * Permissão condicional
 */
export interface ConditionalPermission<TContext = any> {
  /** Condição para verificar a permissão */
  condition: boolean | (() => boolean);

  /** Ação da permissão */
  action: PermissionAction;

  /** Recurso opcional */
  resource?: ResourceType;

  /** Contexto adicional */
  context?: TContext;

  /** Valor padrão quando condição é false */
  defaultValue?: boolean;
}

/**
 * Resultado de verificação condicional
 */
export interface ConditionalResult {
  /** Se a permissão foi concedida */
  allowed: boolean;

  /** Se a condição foi atendida */
  conditionMet: boolean;

  /** Se a verificação foi pulada devido à condição */
  skipped: boolean;

  /** Razão da decisão */
  reason: string;
}

/**
 * Hook que verifica permissões apenas quando certas condições são verdadeiras
 * Útil para otimizar verificações e implementar lógica condicional complexa
 *
 * @param permissions - Array de permissões condicionais
 * @returns Mapa de resultados condicionais
 *
 * @example
 * ```tsx
 * // Verificar permissões baseadas em condições
 * function ConditionalActions({ post, user }) {
 *   const isAuthor = post.authorId === user.id
 *   const isPublished = post.status === "published"
 *   const isDraft = post.status === "draft"
 *
 *   const permissions = useConditionalPermissions([
 *     {
 *       // Só verifica se pode editar se for o autor
 *       condition: isAuthor,
 *       action: "posts.edit",
 *       resource: "posts",
 *       context: { postId: post.id }
 *     },
 *     {
 *       // Só verifica se pode publicar se for rascunho
 *       condition: isDraft,
 *       action: "posts.publish",
 *       resource: "posts"
 *     },
 *     {
 *       // Só verifica se pode despublicar se estiver publicado
 *       condition: isPublished,
 *       action: "posts.unpublish",
 *       resource: "posts",
 *       defaultValue: false // Nega por padrão se condição não for atendida
 *     }
 *   ])
 *
 *   return (
 *     <div>
 *       {permissions[0].allowed && (
 *         <Button onClick={handleEdit}>
 *           Editar (você é o autor)
 *         </Button>
 *       )}
 *
 *       {permissions[1].allowed && (
 *         <Button onClick={handlePublish}>
 *           Publicar Rascunho
 *         </Button>
 *       )}
 *
 *       {permissions[2].conditionMet && permissions[2].allowed && (
 *         <Button onClick={handleUnpublish}>
 *           Despublicar
 *         </Button>
 *       )}
 *     </div>
 *   )
 * }
 * ```
 */
export function useConditionalPermissions<TContext = any>(
  permissions: ConditionalPermission<TContext>[],
): ConditionalResult[] {
  const { can, user } = useACLContext<TContext>();

  return useMemo(() => {
    return permissions.map((perm) => {
      // Avalia a condição
      const conditionMet =
        typeof perm.condition === "function"
          ? perm.condition()
          : perm.condition;

      // Se a condição não for atendida
      if (!conditionMet) {
        return {
          allowed: perm.defaultValue ?? true, // Por padrão, permite se condição não for atendida
          conditionMet: false,
          skipped: true,
          reason: "Condition not met - permission check skipped",
        };
      }

      // Condição atendida, verifica a permissão
      const allowed = can(perm.action, perm.resource, perm.context);

      return {
        allowed,
        conditionMet: true,
        skipped: false,
        reason: allowed
          ? "Condition met and permission granted"
          : "Condition met but permission denied",
      };
    });
  }, [permissions, can, user]);
}

/**
 * Hook para uma única permissão condicional
 *
 * @param condition - Condição para verificar
 * @param action - Ação a verificar
 * @param resource - Recurso opcional
 * @param context - Contexto adicional
 * @param defaultValue - Valor padrão quando condição é false
 * @returns Resultado da verificação condicional
 *
 * @example
 * ```tsx
 * // Verificar permissão apenas se usuário for premium
 * const premiumFeature = useConditionalPermission(
 *   user.isPremium,
 *   "features.premium",
 *   undefined,
 *   undefined,
 *   false // Nega acesso se não for premium
 * )
 *
 * if (!premiumFeature.conditionMet) {
 *   return <UpgradePrompt />
 * }
 *
 * if (!premiumFeature.allowed) {
 *   return <AccessDenied />
 * }
 *
 * return <PremiumFeature />
 * ```
 */
export function useConditionalPermission<TContext = any>(
  condition: boolean | (() => boolean),
  action: PermissionAction,
  resource?: ResourceType,
  context?: TContext,
  defaultValue?: boolean,
): ConditionalResult {
  const results = useConditionalPermissions([
    { condition, action, resource, context, defaultValue },
  ]);

  return results[0];
}

/**
 * Hook que cria uma função para verificação condicional de permissões
 * Útil quando você precisa verificar permissões dinamicamente
 *
 * @returns Função para verificar permissões condicionalmente
 *
 * @example
 * ```tsx
 * const checkConditional = useConditionalChecker()
 *
 * // Em um evento
 * const handleAction = (item) => {
 *   const canProcess = checkConditional(
 *     item.status === "pending",
 *     "items.process",
 *     "items",
 *     { itemId: item.id }
 *   )
 *
 *   if (!canProcess.conditionMet) {
 *     alert("Item não está pendente")
 *     return
 *   }
 *
 *   if (!canProcess.allowed) {
 *     alert("Sem permissão para processar")
 *     return
 *   }
 *
 *   processItem(item)
 * }
 * ```
 */
export function useConditionalChecker<TContext = any>() {
  const { can } = useACLContext<TContext>();

  return useCallback(
    (
      condition: boolean | (() => boolean),
      action: PermissionAction,
      resource?: ResourceType,
      context?: TContext,
      defaultValue: boolean = true,
    ): ConditionalResult => {
      const conditionMet =
        typeof condition === "function" ? condition() : condition;

      if (!conditionMet) {
        return {
          allowed: defaultValue,
          conditionMet: false,
          skipped: true,
          reason: "Condition not met - permission check skipped",
        };
      }

      const allowed = can(action, resource, context);

      return {
        allowed,
        conditionMet: true,
        skipped: false,
        reason: allowed
          ? "Condition met and permission granted"
          : "Condition met but permission denied",
      };
    },
    [can],
  );
}

/**
 * Hook que combina múltiplas condições com operadores lógicos
 *
 * @param mode - "all" (AND) ou "any" (OR)
 * @param conditions - Array de condições
 * @param action - Ação a verificar
 * @param resource - Recurso opcional
 * @param context - Contexto adicional
 * @returns Resultado da verificação
 *
 * @example
 * ```tsx
 * // Verificar se TODAS as condições são verdadeiras
 * const canPerformComplexAction = useCombinedConditionalPermission(
 *   "all",
 *   [
 *     user.isVerified,
 *     user.accountAge > 30,
 *     !user.isSuspended
 *   ],
 *   "actions.complex"
 * )
 *
 * // Verificar se QUALQUER condição é verdadeira
 * const canAccessFeature = useCombinedConditionalPermission(
 *   "any",
 *   [
 *     user.isPremium,
 *     user.isAdmin,
 *     user.hasTrialAccess
 *   ],
 *   "features.special"
 * )
 * ```
 */
export function useCombinedConditionalPermission<TContext = any>(
  mode: "all" | "any",
  conditions: Array<boolean | (() => boolean)>,
  action: PermissionAction,
  resource?: ResourceType,
  context?: TContext,
  defaultValue: boolean = true,
): ConditionalResult {
  const combinedCondition = useMemo(() => {
    const evaluatedConditions = conditions.map((c) =>
      typeof c === "function" ? c() : c,
    );

    return mode === "all"
      ? evaluatedConditions.every((c) => c)
      : evaluatedConditions.some((c) => c);
  }, [mode, conditions]);

  return useConditionalPermission(
    combinedCondition,
    action,
    resource,
    context,
    defaultValue,
  );
}
